import { router } from '@/core/router';
import pageStore from '@/shared/content/page.store';
import authStore from '@/shared/auth/auth.store';
import utilities from '@/shared/utilities.service';
import { TempBasketViewModel } from '@/shared/checkout/basket/temp-basket-api';
import { Ref } from 'vue';
import { GtmTrackingData, GA4AddToCart, GA4Item, GA4TrackLogin, GtmAddPaymentInfo, GtmAddShippingInfo, GtmBeginCheckout, GtmPurchase, GtmRemoveFromCart, GtmViewCart, NavigationTrackingData, GtmItemListName, GtmItemListId, GA4ViewItemList, GA4SelectItem, GA4ViewItem, GA4PageViewEvent, GtmAddToCartProduct, GtmOldAddToCartVariant } from '@/shared/tracking/gtm.model';
import { OutletProductDetailsModel, ProductAddOnModel, ProductDetailModel, ProductSpotModel } from '@/types/productServerApi';
import { BaseProductItem, OrderReceiptItemModel, PersonalOfferRegularItemModel, PromoCodeModel, WishListItem } from '@/b2c/shared/api/server-apis';
import { ProductSpotSearchResultLegacy } from '@/shared/product/spot/useProductSpotLegacyDataMapping';
import siteContextService from '@/b2c/shared/site-context.service';

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
    }
}

export interface GtmVirtualPageViewEvent extends GtmTrackingData {
    event: 'spaPageView';
    url: string;
    title: string;
    pageType: string;
    userContext: string;
    pageId: string;
    itemsInBasket: string;
    basketValue: string;
    offer: string;
}

export interface GtmProductDetailVariant extends GtmTrackingData {
    event: 'productDetail';
    ecommerce: {
        currencyCode: string;
        detail: {
            products: [
                {
                    brand: string | undefined; // designer
                    category: string | undefined;
                    dimension8: string | undefined; // EAN
                    dimension14: string | undefined; // family
                    id: string;
                    name: string;
                    price: number; // in local currency
                    variant: string | undefined; // color
                }
            ]
        }
    };
}

interface GTMSearch extends GtmTrackingData {
    SearchAction: string,
    SearchContentType: string,
    SearchKeyword: string,
    SearchType: string,
    SearchProductCount?: number,
    SearchProduct?: string,
}

const route = router.currentRoute;
class GtmService {
    private getQuantity(item: BaseProductItem | ProductSpotModel | OrderReceiptItemModel | ProductDetailModel | ProductAddOnModel): number {
        if ((item as BaseProductItem | OrderReceiptItemModel).quantity) {
            return (item as BaseProductItem | OrderReceiptItemModel).quantity;
        } else {
            return 1;
        }
    }

    public getGA4Item(options: {product: BaseProductItem, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.sku,
            item_name: product.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount: product.price?.discounts?.reduce((acc, discount) => acc + discount.discount.rawAmount, 0) ?? 0,
            index,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price?.salesPriceTotal?.rawAmount,
            quantity: this.getQuantity(product),
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromProductSpotModel(options: {product: ProductSpotModel, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.sku,
            item_name: product.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount: product.price.discount?.discount.rawAmount,
            index,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price?.salesPrice?.rawAmount,
            quantity: this.getQuantity(product),
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromOrderReceiptItemModel(options: {product: OrderReceiptItemModel, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.sku,
            item_name: product.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount: product.price.discounts.reduce((acc, discount) => acc + discount.discount.rawAmount, 0) ?? 0,
            index,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price?.salesPriceTotal?.rawAmount,
            quantity: this.getQuantity(product),
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromProductAddOnModel(options: {product: ProductAddOnModel, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;

        return {
            item_id: product.sku,
            item_name: product.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount: product.price.discount?.discount.rawAmount,
            index,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price?.salesPriceTotal?.rawAmount,
            quantity: this.getQuantity(product),
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromProductDetailModel(options: {product: ProductDetailModel, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule: boolean, quantity: number}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.id,
            item_name: product.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            discount: product.price.discount?.discount.rawAmount ?? 0,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            price: product.price.salesPrice.rawAmount,
            quantity: 1,
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            index,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromOutletProductDetailsModel(options: {product: OutletProductDetailsModel, promoCodes?: PromoCodeModel[], index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean, quantity: number}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.inventSerial,
            item_name: product.fullName,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            discount: product.price.discount?.discount?.rawAmount ?? 0,
            item_brand: product.metaData?.brand,
            item_category: product.metaData?.businessArea,
            item_category_2: product.metaData?.retailHierarchy,
            item_category_3: product.metaData?.family,
            price: product.price.salesPrice.rawAmount,
            quantity: 1,
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            index,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromLegacyProductSpot(options: {product: ProductSpotSearchResultLegacy, promoCodes?: PromoCodeModel[], discount?: number, index?: number, shippingTier?: string, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule: boolean}): GA4Item {
        const { product, promoCodes, discount, index, itemListId, itemListName, shippingTier, isModule } = options;
        return {
            item_id: product.id,
            item_name: product.title,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount,
            index,
            item_brand: product.designer,
            item_category: product?.metaData?.businessArea,
            item_category_2: product?.metaData?.retailHierarchy,
            item_category_3: product?.metaData?.family,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.salesPrice?.raw.amount,
            quantity: undefined,
            items_shipping_tier: shippingTier,
        };
    }

    public getGA4ItemFromPersonalOfferRegularItemModel(options: {product: PersonalOfferRegularItemModel, index?: number, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, index, itemListId, itemListName, isModule } = options;
        return {
            item_id: product.variant.sku,
            item_name: product.variant.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            discount: product.price.listPriceTotal?.rawAmount && product.price.salesPriceTotal?.rawAmount ? product.price.listPriceTotal?.rawAmount! - product.price.salesPriceTotal?.rawAmount : 0,
            index,
            item_brand: product.variant.designerName,
            item_category: product.variant.businessArea,
            item_category_2: product.variant.retailHierarchy,
            item_category_3: product.variant.familyName,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price.salesPriceTotal?.rawAmount,
            quantity: product.quantity,
        };
    }

    public getGA4ItemFromWishListItem(options: {product: WishListItem, promoCodes?: PromoCodeModel[], index?: number, itemListId?: GtmItemListId, itemListName?: GtmItemListName, isModule?: boolean}): GA4Item {
        const { product, promoCodes, index, itemListId, itemListName, isModule } = options;
        return {
            item_id: product.variant.sku,
            item_name: product.variant.name,
            part_of_configuration: isModule ? 'yes' : 'no',
            affiliation: 'online',
            coupon: promoCodes?.map((promo) => promo.redeemCode).join(', '),
            discount: product.price.discount?.discount.rawAmount ?? 0,
            index,
            item_brand: product.variant.designerName,
            item_category: product.variant.businessArea,
            item_category_2: product.variant.retailHierarchy,
            item_category_3: product.variant.familyName,
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_variant: undefined,
            price: product.price?.salesPrice.rawAmount,
            quantity: 1,
        };
    }

    async pageView(basket: Ref<TempBasketViewModel>): Promise<void> {
        const pageView: GtmVirtualPageViewEvent = {
            event: 'spaPageView',
            url: window.location.origin + route.value.fullPath,
            title: pageStore.title,
            pageType: pageStore.metadata?.pageType ?? '',
            userContext: authStore.userData?.aud ?? 'anonymous',
            pageId: pageStore.metadata?.pageId.toString() ?? '',
            itemsInBasket: utilities.getBasketQuantityFromLineItems(basket?.value.lineItems).toString(),
            basketValue: basket.value?.total.raw.amount.toString() ?? '',
            offer: basket.value?.isQuotation?.toString() ?? 'false',
        };

        this.track(pageView);
    }

    async pageViewGA4(): Promise<void> {
        const pageViewEvent: GA4PageViewEvent = {
            userData: {
                userId: authStore.userData?.aud ? await this.sha256(authStore.userData?.aud) : 'anonymous',
                customerType: authStore.isSignedInAsPartner ? 'business' : 'private',
                loginStatus: authStore.isSignedIn ? 'logged in' : 'not logged in',
            },
            pageCountry: siteContextService.country.code,
            pageSection: pageStore.breadcrumb?.map((breadcrumb) => breadcrumb.name).join(' > ') ?? '',
            previousPageUrl: router.options.history.state.back as string ?? '',
        };

        this.track(pageViewEvent);
    }

    async login(userId: string) {
        const loginEvent: GA4TrackLogin = {
            event: 'trackLogin',
            userData: {
                userId: await this.sha256(userId),
                customerType: 'private',
                loginStatus: 'logged in',
            },
        };
        this.track(loginEvent);
    }

    async goodThingsToComeVideo(action: string): Promise<void> {
        this.track({
            event: 'Video',
            eventAction: action,
            eventCategory: 'Video',
        });
    }

    async megaMenu(trackingData: NavigationTrackingData): Promise<void> {
        this.track({
            event: 'GAEvent',
            eventAction: trackingData.action,
            eventCategory: trackingData.category,
            eventLabel: trackingData.label,
            eventValue: trackingData.value,
        });
    }

    async searchPage(searchTerm: string, productCount: number): Promise<void> {
        const searchEvent: GTMSearch = {
            event: 'SiteSearch',
            SearchAction: 'Searched',
            SearchContentType: '',
            SearchKeyword: searchTerm,
            SearchProductCount: productCount,
            SearchType: 'SearchResult',
        };
        this.track(searchEvent);
    }

    async searchResultProduct(searchTerm: string, productTitle: string): Promise<void> {
        const searchEvent: GTMSearch = {
            event: 'SiteSearch',
            SearchAction: 'Clicked',
            SearchContentType: 'Product',
            SearchKeyword: searchTerm,
            SearchProduct: productTitle,
            SearchType: 'SearchSuggest',
        };
        this.track(searchEvent);
    }

    async ecommerce(product: ProductDetailModel): Promise<void> {
        const ecommerceEvent: GtmProductDetailVariant = {
            event: 'productDetail',
            ecommerce: {
                currencyCode: product.price.salesPrice.currency,
                detail: {
                    products: [
                        {
                            brand: product.designer,
                            category: '',
                            dimension8: product.specifications.ean,
                            dimension14: product.familyName,
                            id: product.id,
                            name: product.fullName,
                            price: product.price.salesPrice.rawAmount,
                            variant: undefined,
                        },
                    ],
                },
            },
        };

        this.track(ecommerceEvent);
    }

    async addToCart(products: GtmAddToCartProduct[], currencyCode: string): Promise<void> {
        const ecommerceEvent: GtmOldAddToCartVariant = {
            event: 'addToCart',
            ecommerce: {
                currencyCode,
                add: {
                    products,
                },
            },
        };
        this.track(ecommerceEvent);
    }

    async viewCart(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string}): Promise<void> {
        const ecommerceEvent: GtmViewCart = {
            event: 'view_cart',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async removeFromCart(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string}): Promise<void> {
        const ecommerceEvent: GtmRemoveFromCart = {
            event: 'remove_from_cart',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async beginCheckout(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string}): Promise<void> {
        const ecommerceEvent: GtmBeginCheckout = {
            event: 'begin_checkout',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async addShippingInfo(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string}): Promise<void> {
        const ecommerceEvent: GtmAddShippingInfo = {
            event: 'add_shipping_info',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async ga4AddToCart(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string}): Promise<void> {
        const ecommerceEvent: GA4AddToCart = {
            event: 'add_to_cart',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async addPaymentInfo(products: GA4Item[], options: {currency?: string, value?: number, 'shipping_tier'?: string, coupon?: string, 'payment_type'?: string}): Promise<void> {
        const ecommerceEvent: GtmAddPaymentInfo = {
            event: 'add_payment_info',
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    async purchase(
        products: GA4Item[],
        options: {
            currency?: string,
            value?: number,
            'shipping_tier'?: string,
            coupon?: string,
            'payment_type'?: string,
            'transaction_id'?: string,
            'tax'?: number,
            'shipping'?: number,
        },
        userData: {
            userId?: string,
            customerType?: string,
            loginStatus?: string,
            country?: string,
            firstname?: string,
            lastname?: string,
            phoneNumber?: string,
            postCode?: string,
            street?: string,
        }): Promise<void> {
        const hashedUserData = await Promise.all([
            this.sha256(userData.userId),
            this.sha256(userData.country),
            this.sha256(userData.firstname),
            this.sha256(userData.lastname),
            this.sha256(userData.phoneNumber),
            this.sha256(userData.postCode),
            this.sha256(userData.street),
        ]);
        const ecommerceEvent: GtmPurchase = {
            event: 'purchase',
            userData: {
                userId: hashedUserData[0],
                country: hashedUserData[1],
                firstname: hashedUserData[2],
                lastname: hashedUserData[3],
                phoneNumber: hashedUserData[4],
                postCode: hashedUserData[5],
                street: hashedUserData[6],
                customerType: userData.customerType,
                loginStatus: userData.loginStatus,
            },
            ecommerce: {
                ...options,
                items: products,
            },
        };

        this.track(ecommerceEvent);
    }

    purchaseOld(
        products: {
        name: string
        id: string
        price: number
        brand: string
        dimension8: string
        dimension14: string
        quantity: number
    }[],
        currencyCode: string,
        options: {
        coupon: string,
        shipping: number,
        tax: number,
        revenue: number,
        id: string
    }) {
        this.track({
            event: 'purchase',
            currencyCode,
            ecommerce: {
                purchase: {
                    actionField: {
                        ...options,
                        action: 'purchase',
                    },
                    products,
                },
            },
        });
    }

    private isProductSpotSearchResultLegacy(product: ProductSpotModel | ProductSpotSearchResultLegacy) {
        return (product as ProductSpotSearchResultLegacy).title !== undefined;
    }

    viewItemList(products: ProductSpotModel[] | ProductSpotSearchResultLegacy[], options: {itemListId: GtmItemListId, itemListName: GtmItemListName}): void {
        const items = products.map((product: ProductSpotModel | ProductSpotSearchResultLegacy, index: number) => {
            if (this.isProductSpotSearchResultLegacy(product)) {
                return this.getGA4ItemFromLegacyProductSpot({
                    product: product as ProductSpotSearchResultLegacy,
                    isModule: false,
                    index,
                    ...options,
                });
            } else {
                return this.getGA4ItemFromProductSpotModel({
                    product: product as ProductSpotModel,
                    isModule: false,
                    index,
                    ...options,
                });
            }
        });
        const ecommerceEvent: GA4ViewItemList = {
            event: 'view_item_list',
            ecommerce: {
                ...options,
                items,
            },
        };

        this.track(ecommerceEvent);
    }

    selectItem(product: ProductSpotModel | ProductSpotSearchResultLegacy, options?: {itemListId?: GtmItemListId, itemListName?: GtmItemListName, index?: number}): void {
        let item: GA4Item;
        if (this.isProductSpotSearchResultLegacy(product)) {
            item = this.getGA4ItemFromLegacyProductSpot({
                product: product as ProductSpotSearchResultLegacy,
                isModule: false,
                ...options,
            });
        } else {
            item = this.getGA4ItemFromProductSpotModel({
                product: product as ProductSpotModel,
                isModule: false,
                ...options,
            });
        }
        const ecommerceEvent: GA4SelectItem = {
            event: 'select_item',
            ecommerce: {
                ...options,
                items: [item],
            },
        };

        this.track(ecommerceEvent);
    }

    viewItem(product: ProductDetailModel): void {
        const item = this.getGA4ItemFromProductDetailModel({
            product,
            isModule: false,
            quantity: 1,
        });
        const ecommerceEvent: GA4ViewItem = {
            event: 'view_item',
            ecommerce: {
                currency: product.price.salesPrice.currency,
                value: product.price.salesPrice.rawAmount,
                items: [item],
            },
        };

        this.track(ecommerceEvent);
    }

    viewOutletItem(product: OutletProductDetailsModel): void {
        const item = this.getGA4ItemFromOutletProductDetailsModel({
            product,
            isModule: false,
            quantity: 1,
        });
        const ecommerceEvent: GA4ViewItem = {
            event: 'view_item',
            ecommerce: {
                currency: product.price.salesPrice.currency,
                value: product.price.salesPrice.rawAmount,
                items: [item],
            },
        };

        this.track(ecommerceEvent);
    }

    private track(data: GtmTrackingData | any): void {
        // console.log('GTM', data);
        const dl = window.dataLayer || [];
        dl.push({ ecommerce: null });
        dl.push(data);
    }

    async sha256(message) {
        // encode as UTF-8
        const msgBuffer = new TextEncoder().encode(message);

        // hash the message
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

        // convert ArrayBuffer to Array
        const hashArray = Array.from(new Uint8Array(hashBuffer));

        // convert bytes to hex string
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        return hashHex;
    }
}

export default new GtmService();
